/**
 * Le pont entre « l'utilisateur a telle langue » et « i18next, change de langue ».
 *
 * ## Pourquoi ce module minuscule existe
 *
 * `ContextProvider` importait `../i18n` pour deux lignes (aligner la langue de
 * l'UI admin sur la préférence du compte). Or `i18n.ts` appelle `.init()` **au
 * chargement du module**, avec ses 24 espaces de noms déclarés — i18next les
 * précharge alors tous.
 *
 * Résultat mesuré sur `m.localhost` : **608 Ko en 24 requêtes** au démarrage de
 * l'app mobile, dont `settings.json` (120 Ko), `products.json` (68 Ko) et
 * `flows.json` (56 Ko) — les traductions d'écrans que le mobile ne porte pas et ne
 * portera pas. Aucun écran mobile n'est traduit : #73 a tranché que le socle écrit
 * ses libellés en français directement.
 *
 * C'est le mode d'échec exact que #23 avait nommé en écartant le second point
 * d'entrée comme garde-fou : **le tree-shaking ne supprime pas les modules à
 * effets de bord**. Et `mobile:check` ne pouvait rien voir — `contexts/` est une
 * zone partagée, légitimement atteinte.
 *
 * ## Comment il coupe
 *
 * L'appel est **injecté par le point d'entrée**, pas importé par le module
 * partagé. `main.tsx` (web) enregistre l'applicateur ; `mobile/main.tsx`
 * n'enregistre rien, donc `i18n.ts` sort du graphe mobile. `ContextProvider` ne
 * connaît plus la bibliothèque, seulement l'intention.
 *
 * Aucun import ici : c'est un module FEUILLE, contrainte posée par #74 pour que le
 * graphe transitif reste vide.
 */

/** `appLocale` : la langue de l'app mobile du personnel (`users.app_locale`), que seul le mobile lit. */
type Applicateur = (locale: string, appLocale?: string | null) => void;

let applicateur: Applicateur | null = null;

/**
 * La dernière locale demandée avant qu'un applicateur existe.
 *
 * `ContextProvider` appelle `applyLocale` depuis la réponse de `/user`, qui peut
 * arriver avant l'enregistrement si l'ordre d'import changeait un jour. Sans ce
 * report, le web perdrait silencieusement l'alignement de langue — le seul effet
 * que ces deux lignes avaient.
 */
let enAttente: { locale: string; appLocale?: string | null } | null = null;

export function setLocaleApplier(fn: Applicateur): void {
    applicateur = fn;

    if (enAttente !== null) {
        const { locale, appLocale } = enAttente;
        enAttente = null;
        fn(locale, appLocale);
    }
}

/**
 * Appliquer la langue du compte, si quelqu'un sait le faire.
 *
 * Sans applicateur, l'appel ne fait rien et ne coûte rien. Le mobile enregistre
 * le sien (`mobile/main.tsx`), qui lit `appLocale` avec son propre dictionnaire.
 */
export function applyLocale(locale: string, appLocale?: string | null): void {
    if (!applicateur) {
        enAttente = { locale, appLocale };

        return;
    }

    applicateur(locale, appLocale);
}

/** Réservé aux tests : rend le module à son état neuf. */
export function reinitialiserLocaleBridge(): void {
    applicateur = null;
    enAttente = null;
}
